import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import Card, { CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { adminAPI } from '../domains/authorities/api/admin';
import { usePageTitle } from '../hooks/shared';

function statusDot(status) {
  if (status === 'success') return 'bg-emerald-500';
  if (status === 'partial') return 'bg-amber-400';
  if (status === 'error') return 'bg-red-500';
  if (status === 'running') return 'bg-blue-500 animate-pulse';
  return 'bg-neutral-300';
}

function fmtRelative(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function DataSourcesPanel() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-data-sources'],
    queryFn: adminAPI.getDataSources,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[1, 2].map((i) => (
          <div key={i} className="h-14 bg-neutral-100 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError || !data?.dataSources?.length) {
    return <p className="text-sm text-neutral-400 italic">No connections registered yet.</p>;
  }

  return (
    <div className="space-y-3">
      {data.dataSources.map((src) => {
        const stats = src.lastRunStats;
        return (
          <div
            key={src.key}
            className="flex items-start gap-3 p-3 rounded-lg bg-neutral-50 border border-neutral-100"
          >
            <span
              className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${statusDot(src.lastRunStatus)}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{src.name}</span>
                <span className="text-xs text-neutral-400 shrink-0">
                  {src.type === 'scraper' ? 'scraper' : 'api'}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs text-neutral-500">
                <span>Last run: {fmtRelative(src.lastRunAt)}</span>
                {stats && (
                  <>
                    {stats.created > 0 && (
                      <span className="text-emerald-600">+{stats.created} created</span>
                    )}
                    {stats.updated > 0 && (
                      <span className="text-blue-600">{stats.updated} updated</span>
                    )}
                    {stats.errors > 0 && (
                      <span className="text-red-500">{stats.errors} errors</span>
                    )}
                  </>
                )}
                {src.nextRunAt && (
                  <span className="text-neutral-400">Next: {fmtDate(src.nextRunAt)}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  usePageTitle('Dashboard');
  const navigate = useNavigate();

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">Welcome Back!</h1>
          <p className="text-neutral-600">Here's your voter information dashboard</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card patrioticShadow hover className="cursor-pointer" onClick={() => navigate('/vote')}>
            <CardHeader>
              <CardTitle>Voting Information</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-neutral-600">
                Find your voting districts, polling locations, ballot info, and more
              </p>
            </CardContent>
          </Card>

          <Card
            patrioticShadow
            hover
            className="cursor-pointer"
            onClick={() => navigate('/registration')}
          >
            <CardHeader>
              <CardTitle>Voter Registration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-neutral-600">Complete or update your voter registration</p>
            </CardContent>
          </Card>

          <Card
            patrioticShadow
            hover
            className="cursor-pointer"
            onClick={() => navigate('/subscription')}
          >
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-neutral-600">Manage your subscription and premium features</p>
            </CardContent>
          </Card>

          <Card
            patrioticShadow
            hover
            className="cursor-pointer"
            onClick={() => navigate('/settings')}
          >
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-neutral-600">Update your profile and account settings</p>
            </CardContent>
          </Card>
        </div>

        {/* Data Sources */}
        <div className="mt-10">
          <h2 className="text-xl font-serif font-bold mb-4">Data Connections</h2>
          <Card>
            <CardContent className="pt-4">
              <DataSourcesPanel />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
